import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/brand/brand/apps/docs/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import InlineCode from '@primer/gatsby-theme-doctocat/src/components/inline-code';
import { PropTableValues } from '../../src/components';
import { Label } from '@primer/react';
import { Button, Heading, Text } from '@primer/react-brand';
import { SearchIcon } from '@primer/octicons-react';
import { Link } from 'gatsby';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import {FormControl} from '@primer/react-brand'
`}</code></pre>
    <h2>{`Examples`}</h2>
    <h3>{`Basic`}</h3>
    <p><inlineCode parentName="p">{`FormControl`}</inlineCode>{` is responsible for layout and ensuring that relevant IDs and ARIA attributes are passed to its `}<inlineCode parentName="p">{`children`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<FormControl>
  <FormControl.Label>Name</FormControl.Label>
  <TextInput autoComplete="name" />
</FormControl>
`}</code></pre>
    <h3>{`Alternative inputs`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<Stack direction="vertical" gap="spacious">
  {/* Select */}
  <FormControl>
    <FormControl.Label>Select</FormControl.Label>
    <Select defaultValue="">
      <Select.Option value="" disabled>
        Select a handle
      </Select.Option>
      <Select.Option value="mona">Monalisa</Select.Option>
      <Select.Option value="hubot">Hubot</Select.Option>
    </Select>
  </FormControl>
  {/* Checkbox */}
  <FormControl>
    <Checkbox />
    <FormControl.Label>Checkbox</FormControl.Label>
    <FormControl.Hint>With an optional message</FormControl.Hint>
  </FormControl>
  {/* Radio */}
  <FormControl>
    <FormControl.Label>Radio</FormControl.Label>
    <FormControl.Hint>With an optional message</FormControl.Hint>
    <Radio />
  </FormControl>
  {/* Textarea */}
  <FormControl fullWidth>
    <FormControl.Label>Textarea</FormControl.Label>
    <Textarea />
  </FormControl>
</Stack>
`}</code></pre>
    <h2>{`Layout`}</h2>
    <p><inlineCode parentName="p">{`FormControl`}</inlineCode>{` can help compose form layouts quickly, while ensuring the inputs are accessible for assistive technologies.`}</p>
    <p>{`An example of a typical layout composed using `}<inlineCode parentName="p">{`FormControl`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<form>
  <Container
    sx={{
      alignItems: 'center',
      display: 'grid',
      gap: 16,
      margin: '0 auto ',
      maxWidth: 600,
      paddingBottom: 3,
    }}
  >
    <Text as="p" variant="muted" size="100">
      All fields marked with an asterisk (*) are required
    </Text>
    <Container
      sx={{
        display: 'grid',
        gridTemplateColumns: '0.5fr 1fr 1fr',
        gap: 16,
      }}
    >
      <FormControl fullWidth>
        <FormControl.Label>Title</FormControl.Label>
        <Select defaultValue="">
          <Select.Option value="" disabled>
            Title
          </Select.Option>
          <Select.Option value="miss">Miss</Select.Option>
          <Select.Option value="mr">Mr</Select.Option>
          <Select.Option value="mrs">Mrs</Select.Option>
          <Select.Option value="mx">Mx</Select.Option>
        </Select>
      </FormControl>
      <FormControl fullWidth required>
        <FormControl.Label>First name</FormControl.Label>
        <TextInput required autoComplete="given-name" />
      </FormControl>
      <FormControl fullWidth required>
        <FormControl.Label>Last name</FormControl.Label>
        <TextInput required autoComplete="family-name" />
      </FormControl>
    </Container>

    <FormControl fullWidth required>
      <FormControl.Label>Enterprise name</FormControl.Label>
      <TextInput required />
    </FormControl>

    <FormControl fullWidth required>
      <FormControl.Label>Enterprise URL</FormControl.Label>
      <TextInput leadingText="github.com/" required />
    </FormControl>

    <FormControl fullWidth required>
      <FormControl.Label>Country</FormControl.Label>
      <Select defaultValue="">
        <Select.Option value="" disabled>
          Country
        </Select.Option>
        <Select.Option value="us">United States of America</Select.Option>
        <Select.Option value="uk">United Kingdom</Select.Option>
      </Select>
    </FormControl>
    <FormControl hasBorder required>
      <Checkbox />
      <FormControl.Label>
        Contact me about GitHub Enterprise Server
      </FormControl.Label>
      <FormControl.Hint>
        <Text size="200" variant="muted">
          I&apos;m interested in learning more about{' '}
          <InlineLink
            size="200"
            href="https://github.com/enterprise"
            target="_blank"
          >
            GitHub Enterprise Server
          </InlineLink>{' '}
          and would like to be contacted by GitHub’s sales team.
        </Text>
      </FormControl.Hint>
    </FormControl>
    <Container
      sx={{
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'var(--brand-control-color-border-default)',
        backgroundColor: 'var(--brand-color-canvas-inset)',
        height: 150,
        width: '100%',
        borderRadius: 6,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Text size="200" variant="muted">
        Captcha
      </Text>
    </Container>
    <FormControl required>
      <Checkbox />
      <FormControl.Label>
        <Text size="200" variant="muted">
          I hereby accept the{' '}
          <InlineLink
            size="200"
            href="https://github.com/customer-terms"
            target="_blank"
          >
            GitHub Customer Agreement
          </InlineLink>{' '}
          on behalf of my organization and confirm that I have the authority to
          do so. For more information about GitHub&apos;s privacy practices, see
          the{' '}
          <InlineLink
            size="200"
            href="https://docs.github.com/en/site-policy/privacy-policies/github-privacy-statement"
            target="_blank"
          >
            GitHub Privacy Statement.
          </InlineLink>
        </Text>
      </FormControl.Label>
    </FormControl>
    <Container
      sx={{
        justifyContent: 'end',
        display: 'inline-grid',
        gap: 16,
      }}
    >
      <Button variant="primary" type="submit">
        Start trial
      </Button>
    </Container>
  </Container>
</form>
`}</code></pre>
    <h3>{`Visually hidden label`}</h3>
    <p>{`Labels should only be visually hidden when the context is clear from the input itself. For example, a search input with a search icon. The majority of the time, labels should be visible.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<FormControl>
  <FormControl.Label visuallyHidden>Search</FormControl.Label>
  <TextInput type="search" trailingVisual={<SearchIcon />} />
</FormControl>
`}</code></pre>
    <h3>{`Validation`}</h3>
    <p>{`The following example demonstrates declarative form validation in `}<a parentName="p" {...{
        "href": "https://reactjs.org/docs/forms.html#controlled-components"
      }}>{`controlled mode`}</a>{`.`}</p>
    <p>{`When the form is submitted with an invalid value, the invalid input receives focus to help the user correct the error. This is especially important for users navigating the form using a screen reader.`}</p>
    <p>{`More information on form validation best practices can be found in the `}<a parentName="p" {...{
        "href": "https://primer.style/ui-patterns/forms/overview#validation"
      }}>{`Primer UI Patterns documentation`}</a>{`.`}</p>
    <p>{`Try changing the input value to `}<inlineCode parentName="p">{`monalisa`}</inlineCode>{` and submitting the form to show the `}<inlineCode parentName="p">{`success`}</inlineCode>{` state.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "live noinline",
        "live": true,
        "noinline": true
      }}>{`const App = () => {
  const inputRef = React.useRef(null)
  const [value, setValue] = React.useState('mona lisa')
  const [isValid, setIsValid] = React.useState(false)

  const onChange = (e) => setValue(e.target.value)

  const onSubmit = (e) => {
    e.preventDefault()
    const valid = !value.includes(' ')
    setIsValid(valid)

    if (!valid) {
      inputRef.current.focus()
    }
  }

  return (
    <form onSubmit={onSubmit}>
      <Stack gap="normal">
        <FormControl validationStatus={isValid ? 'success' : 'error'} fullWidth>
          <FormControl.Label>GitHub handle</FormControl.Label>
          <TextInput
            ref={inputRef}
            fullWidth
            value={value}
            onChange={onChange}
          />
          {isValid && (
            <FormControl.Validation>Valid name</FormControl.Validation>
          )}
          {!isValid && (
            <FormControl.Validation>
              GitHub handles cannot contain spaces.{' '}
              {value && \`Did you mean "\${value.replaceAll(' ', '')}"\`}
            </FormControl.Validation>
          )}
        </FormControl>
        <Button type="submit">Submit</Button>
      </Stack>
    </form>
  )
}

render(App)
`}</code></pre>
    <h3>{`Hint`}</h3>
    <p><inlineCode parentName="p">{`FormControl.Hint`}</inlineCode>{` can be used to provide additional context or guidance to the user. `}<inlineCode parentName="p">{`FormControl.Hint`}</inlineCode>{` must be a direct child of `}<inlineCode parentName="p">{`FormControl`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<FormControl>
  <FormControl.Label>Select</FormControl.Label>
  <Select>
    <Select.Option value="" disabled>
      Select a handle
    </Select.Option>
    <Select.Option value="mona">Monalisa</Select.Option>
    <Select.Option value="hubot">Hubot</Select.Option>
  </Select>
  <FormControl.Hint>With an optional message</FormControl.Hint>
</FormControl>
`}</code></pre>
    <h3>{`Full width`}</h3>
    <p>{`Pass the `}<inlineCode parentName="p">{`fullWidth`}</inlineCode>{` prop to `}<inlineCode parentName="p">{`FormControl`}</inlineCode>{` to provide additional behavior and state context to all `}<inlineCode parentName="p">{`children`}</inlineCode>{`, rather than the input only.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<FormControl fullWidth>
  <FormControl.Label>Name</FormControl.Label>
  <TextInput autoComplete="name" />
</FormControl>
`}</code></pre>
    <h3>{`Sizes`}</h3>
    <p><inlineCode parentName="p">{`FormControl`}</inlineCode>{` can appear in `}<inlineCode parentName="p">{`medium`}</inlineCode>{` and `}<inlineCode parentName="p">{`large`}</inlineCode>{` dimensions using the `}<inlineCode parentName="p">{`size`}</inlineCode>{` prop.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<Container sx={{display: 'grid', gap: 3}}>
  <FormControl size="medium">
    <FormControl.Label>Medium</FormControl.Label>
    <TextInput />
  </FormControl>

  <FormControl size="large">
    <FormControl.Label>Large</FormControl.Label>
    <TextInput />
  </FormControl>
</Container>
`}</code></pre>
    <h3>{`Required`}</h3>
    <p>{`Pass the `}<inlineCode parentName="p">{`required`}</inlineCode>{` prop to `}<inlineCode parentName="p">{`FormControl`}</inlineCode>{` to provide additional behavior and state context to all `}<inlineCode parentName="p">{`children`}</inlineCode>{`, rather than the input only.`}</p>
    <p>{`When marking a field as required, it is recommended to also provide a corresponding message at the start of the form informing the user that "`}<em parentName="p">{`all fields marked with an asterisk (`}{`*`}{`) are required`}</em>{`".`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<FormControl required>
  <FormControl.Label>Name</FormControl.Label>
  <TextInput autoComplete="name" />
</FormControl>
`}</code></pre>
    <h3>{`Using `}<inlineCode parentName="h3">{`refs`}</inlineCode></h3>
    <p><inlineCode parentName="p">{`FormControl`}</inlineCode>{` inputs can be used in `}<a parentName="p" {...{
        "href": "https://reactjs.org/docs/uncontrolled-components.html"
      }}>{`uncontrolled mode`}</a>{` by forwarding a `}<inlineCode parentName="p">{`ref`}</inlineCode>{` to the underlying element.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "live noinline",
        "live": true,
        "noinline": true
      }}>{`const App = () => {
  const inputRef = React.useRef(null)

  const handleSubmit = (e) => {
    e.preventDefault()
    if (inputRef.current) {
      alert(\`Name: \${inputRef.current.value}\`)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Container
        sx={{
          display: 'grid',
          gap: 'var(--base-size-16)',
          maxWidth: 400,
          marginX: 'auto',
        }}
      >
        <FormControl fullWidth>
          <FormControl.Label>Name</FormControl.Label>
          <TextInput
            ref={inputRef}
            defaultValue="Mona Lisa"
            autoComplete="name"
          />
        </FormControl>
        <Button type="submit" variant="primary">
          Submit
        </Button>
      </Container>
    </form>
  )
}

render(App)
`}</code></pre>
    <h2>{`Component props`}</h2>
    <h3>{`FormControl `}<Label mdxType="Label">{`Required`}</Label></h3>
    <p><inlineCode parentName="p">{`FormControl`}</inlineCode>{` passes contextual data to its child inputs, labels, validation messaging and more.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Name`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": "center"
          }}>{`Default`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`children`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><PropTableValues values={['FormControl.Label', 'Checkbox', 'Select', 'TextInput', 'FormControl.Validation', 'React.ReactElement']} addLineBreaks mdxType="PropTableValues" />{`,`}</td>
          <td parentName="tr" {...{
            "align": "center"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Valid child nodes`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`className`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "center"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Sets a custom class`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`id`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "center"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Sets a custom id`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`fullWidth`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "center"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Stretches elements visually to the edges of its parent container.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`ref`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`React.RefObject`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "center"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Forward a Ref to the underlying DOM node`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`size`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><PropTableValues values={['medium', 'large']} addLineBreaks mdxType="PropTableValues" /></td>
          <td parentName="tr" {...{
            "align": "center"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Visual dimensions for input and label`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`validationStatus`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><PropTableValues values={['error', 'success']} addLineBreaks mdxType="PropTableValues" /></td>
          <td parentName="tr" {...{
            "align": "center"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Applies visual and semantic state to the underlying elements`}</td>
        </tr>
      </tbody>
    </table>
    <h3 id="FormControl-label">
  FormControl.Label <Label mdxType="Label">Required</Label>
    </h3>
    <p><inlineCode parentName="p">{`FormControl.Label`}</inlineCode>{` should be provided for the `}<inlineCode parentName="p">{`FormControl`}</inlineCode>{` to be accessible to assistive technology, but it may be visually hidden.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Name`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": "center"
          }}>{`Default`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`children`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "center"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Label text`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`className`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "center"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Applies a custom class`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`id`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "center"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Sets a custom id`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`ref`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`React.RefObject`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "center"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Forward a Ref to the underlying DOM node`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`size`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><PropTableValues values={['medium', 'large']} addLineBreaks mdxType="PropTableValues" /></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><inlineCode parentName="td">{`'medium'`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Set visual dimensions`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`visuallyHidden`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Hide for sighted users`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`Additional props can be passed to the `}<inlineCode parentName="p">{`<label>`}</inlineCode>{` element. `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/HTML/Element/label#attributes"
      }}>{`See MDN for a list of props`}</a>{` accepted by the `}<inlineCode parentName="p">{`<label>`}</inlineCode>{` element.`}</p>
    <h3 id="FormControl-validation">FormControl.Validation</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Name`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": "center"
          }}>{`Default`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`children`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "center"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Validation message`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`className`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "center"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`FormControl.Validation custom class`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`id`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "center"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Sets a custom id`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`ref`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`React.RefObject`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "center"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Forward a Ref to the underlying DOM node`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`validationStatus`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}><PropTableValues values={['error', 'success']} addLineBreaks mdxType="PropTableValues" /></td>
          <td parentName="tr" {...{
            "align": "center"
          }}></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Applies a visual and semantic validation state to the underlying elements`}</td>
        </tr>
      </tbody>
    </table>
    <h2>{`Related components`}</h2>
    <ul>
      <li parentName="ul">
        <Link to="/components/Checkbox" mdxType="Link">Checkbox</Link>
      </li>
      <li parentName="ul">
        <Link to="/components/Select" mdxType="Link">Select</Link>
      </li>
      <li parentName="ul">
        <Link to="/components/TextInput" mdxType="Link">TextInput</Link>
      </li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      